import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Lock, Eye, Database, Bell, Trash2, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <Helmet>
        <title>Privacy Policy | SharePay India</title>
        <meta name="description" content="SharePay India Privacy Policy - Learn how we collect, use, and protect your personal information. GDPR compliant." />
        <link rel="canonical" href="https://sharepay.today/privacy" />
      </Helmet>

      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-bold gradient-text">Privacy Policy</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground">Last Updated: January 30, 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-foreground">
            {/* Introduction */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-violet-500" /> Introduction
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                SharePay ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Platform"). This policy complies with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 under the Information Technology Act, 2000, and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021 of India, along with reasonable GDPR principles.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                By using SharePay, you consent to the data practices described in this policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-violet-500" /> Information We Collect
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Full name and profile picture</li>
                    <li>Email address and phone number</li>
                    <li>UPI ID (for payment processing)</li>
                    <li>Contact list (optional, for group invites only)</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Financial Information</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Payment history and transaction records</li>
                    <li>Contribution amounts and settlement details</li>
                    <li>Group expense data</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Usage Information</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>App usage patterns and behavioral data</li>
                    <li>Device information and IP address</li>
                    <li>Login timestamps and session data</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-violet-500" /> How We Use Your Information
              </h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>To facilitate group expense tracking and payment reminders</li>
                <li>To process UPI-linked transactions securely</li>
                <li>To send payment reminders and notifications</li>
                <li>To improve our services and user experience</li>
                <li>To detect and prevent fraud or unauthorized access</li>
                <li>To comply with legal obligations</li>
                <li>To communicate important updates about our service</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-violet-500" /> Data Security & Encryption
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-4">
                <li><strong>End-to-end encryption</strong> for all financial transactions</li>
                <li><strong>AES-256 encryption</strong> for data at rest</li>
                <li><strong>TLS 1.3</strong> for data in transit</li>
                <li><strong>Secure authentication</strong> with biometric options</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and audit logging</li>
              </ul>
            </section>

            {/* Third-Party Sharing */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">Third-Party Data Sharing</h2>
              <p className="text-muted-foreground leading-relaxed">
                <strong>We do not sell, trade, or rent your personal information to third parties.</strong>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li><strong>With your explicit consent</strong> for specific purposes</li>
                <li><strong>Payment processors</strong> (UPI/Razorpay) to complete transactions</li>
                <li><strong>Legal requirements</strong> when required by Indian law or court order</li>
                <li><strong>Service providers</strong> who assist in operating our platform under strict confidentiality agreements</li>
              </ul>
            </section>

            {/* Cookies & Analytics */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">Cookies & Analytics</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li>Remember your login preferences</li>
                <li>Analyze usage patterns to improve our service</li>
                <li>Provide personalized content and recommendations</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You can control cookie preferences through your browser settings. Disabling cookies may affect some functionality.
              </p>
            </section>

            {/* Notification Preferences */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-violet-500" /> Notification Preferences
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                You can manage your notification preferences within the app:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li>Payment reminders (timing and frequency)</li>
                <li>Group activity notifications</li>
                <li>Marketing communications (opt-in only)</li>
                <li>Push notifications on/off</li>
              </ul>
            </section>

            {/* User Rights */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-violet-500" /> Your Rights
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Under Indian IT Rules 2021 and GDPR principles, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mt-2">
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Deletion:</strong> Request deletion of your account and all associated data</li>
                <li><strong>Right to Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
                <li><strong>Right to Object:</strong> Object to certain types of data processing</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:support@sharepay.today" className="text-violet-500 hover:underline">support@sharepay.today</a>.
              </p>
            </section>

            {/* Data Retention */}
            <section className="rounded-2xl bg-card border border-border p-6">
              <h2 className="text-xl font-semibold mb-4">Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy. Financial transaction records are retained for 7 years as per Indian regulatory requirements. Upon account deletion, your data will be permanently removed within 30 days, except where retention is required by law.
              </p>
            </section>

            {/* Contact */}
            <section className="rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 p-6 text-white">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" /> Contact Us
              </h2>
              <p className="leading-relaxed opacity-90">
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-white/10 rounded-xl">
                <p className="font-medium">SharePay Privacy Team</p>
                <p className="opacity-90">Email: <a href="mailto:support@sharepay.today" className="underline">support@sharepay.today</a></p>
                <p className="opacity-90 mt-2">Response time: Within 48 hours</p>
              </div>
            </section>

            {/* Footer Note */}
            <p className="text-center text-sm text-muted-foreground">
              This Privacy Policy may be updated from time to time. We will notify you of any material changes via email or in-app notification.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
